// Capa HTTP de "Rellenar con IA": lee el expediente y devuelve una PROPUESTA
// para una pestaña técnica. El estado vive en TanStack Query (ver
// `src/hooks/useRellenoConIa.ts`).

import type {
  EstadoDeInscripcionDelPozo,
  Moneda,
  TipoDeFuenteDeAgua,
  TipoDeObraDeAgua,
} from "./informes";

import { apiFetch } from "./api";

/**
 * Una fuente de agua PROPUESTA por el modelo.
 *
 * NO ES UNA `FuenteDeAguaDelInforme`, y las dos diferencias importan:
 *
 * - No tiene `id`: no existe ninguna fila. Nada se guardó.
 * - No tiene `validada`: una propuesta nace sin validar POR DEFINICIÓN. Dejar
 *   que el servidor mandara ese campo abriría la puerta a que un día llegara en
 *   `true`, y la casilla existe justamente para decir que una persona revisó lo
 *   que la IA propuso.
 *
 * Los tres números llegan como NÚMEROS y no como los strings de `decimal:*` que
 * devuelven los recursos: acá no hay columna que castear, es lo que el modelo
 * respondió.
 */
export interface FuenteDeAguaPropuesta {
  tipo: TipoDeFuenteDeAgua;
  rio_canal: string | null;
  acciones: number | null;
  litros_seg: number | null;
  equivalencia_ls_accion: number | null;
  estado_inscripcion: EstadoDeInscripcionDelPozo | null;
  /** De qué documento dice la IA que salió esta fila. */
  fuente: FuenteDeLaPropuesta | null;
}

export interface ObraDeAguaPropuesta {
  tipo: TipoDeObraDeAgua;
  capacidad_descripcion: string | null;
  /** De qué documento dice la IA que salió esta fila. */
  fuente: FuenteDeLaPropuesta | null;
}

/**
 * El documento del expediente del que la IA DICE haber sacado una fila o un
 * texto. Es una declaración del modelo, no una prueba: el esquema le impide
 * inventar un documento, no elegir el equivocado. `null` es "no lo indicó".
 * Solo existe mientras la propuesta se revisa; no se guarda con los datos.
 */
export interface FuenteDeLaPropuesta {
  tipo: string;
  archivo: string;
}

/** Un documento del expediente que el modelo SÍ leyó. */
export interface DocumentoLeidoPorLaIa {
  nombre: string;
  /** El tipo del catálogo ("Certificado inscripción derechos de agua superficial"). */
  tipo: string;
  /** No entró entero: pasó el techo de contexto. */
  recortado: boolean;
}

/**
 * Un documento que se PODÍA leer y no se leyó.
 *
 * NO ES UN `DocumentoLeidoPorLaIa` SIN `recortado`, aunque se le parezca: ese
 * campo dice cuánto del documento entró, y acá no entró nada. Mandarlo en
 * `false` sería afirmar que se leyó entero.
 *
 * Hasta el 21-09-2026 de cada dato se leía UN documento —el primero de su
 * lista que estuviera y se pudiera leer—, así que los de menor prioridad
 * quedaban afuera en silencio: quien subía una tasación de Consultek a un
 * informe que ya tenía una externa veía volver la propuesta vacía y no tenía
 * cómo saber por qué.
 *
 * Sin tope (21-09-2026), esto ya casi no le pasa a un documento suelto: cada
 * campo lee TODOS sus candidatos legibles. Lo único que sigue pudiendo perder
 * —y por lo tanto lo único que sigue pudiendo aparecer acá— es una tasación
 * anterior que perdió contra otra más reciente dentro de su propio grupo.
 */
export interface DocumentoSinUsar {
  nombre: string;
  tipo: string;
}

/**
 * Lo que TODA propuesta de relleno trae, sea cual sea la pestaña.
 *
 * Las seis pestañas técnicas devuelven la misma envoltura y cambian solo en su
 * `propuesta`. Declarar estos cuatro campos una vez es lo que hace que la
 * séptima pestaña no pueda nacer con uno de menos: hasta el 15-09-2026 estaban
 * retipeados en las seis, y agregar `sin_usar` costó seis ediciones idénticas
 * —que es exactamente la señal de que sobraban cinco—.
 *
 * NO incluye `propuesta`: es lo único que de verdad distingue a una pestaña de
 * otra, y meterlo acá como `unknown` obligaría a castear en los seis llamadores.
 */
interface PropuestaDeRelleno {
  ejecucion_id: number;

  /**
   * QUÉ SE LEYÓ, y no es un extra decorativo.
   *
   * Una propuesta vacía es indistinguible de un expediente al que le falta el
   * certificado de derechos de agua. Con esta lista, quien tasa ve que se
   * leyeron la escritura y la tasación anterior, que el certificado no estaba, y
   * sabe qué pedirle al cliente.
   */
  documentos: DocumentoLeidoPorLaIa[];

  /**
   * Cuántos documentos que SÍ sirven para la pestaña quedaron sin leer: o
   * esperando al transcriptor, o fuera del techo de contexto.
   *
   * Es la diferencia entre "el predio no tiene agua" y "el papel que lo dice
   * todavía no se puede leer", que se resuelven al revés.
   */
  pendientes: number;

  /**
   * Los que se PODÍAN leer y no se leyeron, porque para cada dato que podían
   * traer ya había un documento de mayor prioridad.
   *
   * Es el tercer caso, distinto de los otros dos y con otra salida: acá no hay
   * nada que pedirle al cliente ni nada que esperar — el papel está y se puede
   * leer. Lo que hay que decidir es cuál de los dos se quiere.
   */
  sin_usar: DocumentoSinUsar[];
}

export interface PropuestaDeAguas extends PropuestaDeRelleno {
  propuesta: {
    fuentes: FuenteDeAguaPropuesta[];
    obras: ObraDeAguaPropuesta[];
  };
}

export async function rellenarAguasConIa(informeId: number): Promise<PropuestaDeAguas> {
  return apiFetch<PropuestaDeAguas>(`/api/informes/${informeId}/agua/rellenar`, {
    method: "POST",
  });
}

/* --- "Descripción de suelos" ------------------------------------------------ */

/**
 * Una fila del cuadro de clases de uso de suelo PROPUESTA por el modelo.
 *
 * VIENEN LOS DOS, `suelo_clase_id` y `clase`, y cada uno tiene su trabajo: el id
 * es lo que el formulario guarda —su selector elige por id— y el código es lo
 * que se DIBUJA mientras la persona revisa, antes de que exista ninguna fila.
 *
 * La traducción de código a id la hace el SERVIDOR contra el catálogo, igual que
 * el importador de tasaciones resuelve la comuna contra el maestro: un id que
 * viniera del modelo podría apuntar a otra clase y la propuesta se vería
 * idéntica a una legítima.
 */
export interface ClaseDeSueloPropuesta {
  suelo_clase_id: number;
  clase: string;
  descripcion: string | null;
  /** En la unidad que declara el INFORME, no en hectáreas fijas. */
  superficie: number | null;
  /** De qué documento dice la IA que salió esta fila. */
  fuente: FuenteDeLaPropuesta | null;
}

export interface PropuestaDeSuelos extends PropuestaDeRelleno {
  propuesta: {
    clases: ClaseDeSueloPropuesta[];
    topografia: string | null;
    /** Solo hay fuente si se propuso texto. */
    topografia_fuente: FuenteDeLaPropuesta | null;
    descripcion_general: string | null;
    descripcion_general_fuente: FuenteDeLaPropuesta | null;
  };
}

export async function rellenarSuelosConIa(informeId: number): Promise<PropuestaDeSuelos> {
  return apiFetch<PropuestaDeSuelos>(`/api/informes/${informeId}/suelos/rellenar`, {
    method: "POST",
  });
}

/* --- "Maquinarias y equipos" ------------------------------------------------ */

/**
 * Una máquina PROPUESTA por el modelo.
 *
 * TODO LLEGA COMO NÚMERO O `null`, no como el string de un `<input>`: la
 * traducción al formulario la hace `maquinariasConIa.ts`, que es donde vive la
 * regla del punto de miles.
 *
 * `moneda` PUEDE VENIR EN `null` aunque el request la exija, y eso es correcto:
 * el formulario arranca en USD, así que el trabajo de elegir por omisión ya está
 * hecho allá. Devolver `null` es cómo el modelo dice "el documento no lo declara"
 * — y suponerla multiplica o divide por mil un valor sin que nada se vea raro.
 */
export interface MaquinaPropuesta {
  nombre: string;
  anio_fabricacion: number | null;
  unidades: number | null;
  vida_util_total: number | null;
  vida_util_residual: number | null;
  /** El valor de UNA unidad, nunca el de la fila. */
  valor_unitario_reposicion: number | null;
  valor_unitario_residual: number | null;
  /** Con signo: un descuento va negativo. */
  ajuste_comercial_pct: number | null;
  moneda: Moneda | null;
  /** De qué documento dice la IA que salió esta fila. */
  fuente: FuenteDeLaPropuesta | null;
}

export interface PropuestaDeMaquinarias extends PropuestaDeRelleno {
  propuesta: {
    maquinas: MaquinaPropuesta[];
    descripcion_general: string | null;
    /** Solo hay fuente si se propuso texto. */
    descripcion_general_fuente: FuenteDeLaPropuesta | null;
  };
}

export async function rellenarMaquinariasConIa(
  informeId: number,
): Promise<PropuestaDeMaquinarias> {
  return apiFetch<PropuestaDeMaquinarias>(`/api/informes/${informeId}/maquinarias/rellenar`, {
    method: "POST",
  });
}

/* --- "Instalaciones y obras complementarias" -------------------------------- */

/**
 * Una obra PROPUESTA por el modelo.
 *
 * NO TIENE `moneda` NI `nota_terreno`, y las dos ausencias están decididas:
 * todo va en UF —la obra civil se presupuesta así en Chile— y la nota es lo que
 * anotó quien fue a ver la obra, que el modelo no es.
 *
 * `cantidad` ES DECIMAL, al revés que las `unidades` de una máquina: aquellas se
 * cuentan y esta se MIDE (mil metros cuadrados de losa).
 */
export interface ObraPropuesta {
  tipo: string;
  anio: number | null;
  cantidad: number | null;
  /** En UF, el de UNA unidad. */
  valor_reposicion_uf_un: number | null;
  /** En UF y INDEPENDIENTE del de reposición: no se deriva de él. */
  valor_comercial_uf_un: number | null;
  /** De qué documento dice la IA que salió esta fila. */
  fuente: FuenteDeLaPropuesta | null;
}

export interface PropuestaDeInstalaciones extends PropuestaDeRelleno {
  propuesta: {
    instalaciones: ObraPropuesta[];
    descripcion_general: string | null;
    /** Solo hay fuente si se propuso texto. */
    descripcion_general_fuente: FuenteDeLaPropuesta | null;
  };
}

export async function rellenarInstalacionesConIa(
  informeId: number,
): Promise<PropuestaDeInstalaciones> {
  return apiFetch<PropuestaDeInstalaciones>(
    `/api/informes/${informeId}/instalaciones/rellenar`,
    { method: "POST" },
  );
}

/* --- "Construcciones" ------------------------------------------------------- */

/**
 * Una construcción PROPUESTA por el modelo.
 *
 * LOS DOS VALORES VAN EN UF POR METRO CUADRADO, y eso es lo que hay que tener
 * presente al mirar esta interfaz: el certificado de avalúo del SII —el
 * documento que MANDA en esta pestaña— publica una cifra de dinero que NO es
 * ninguno de los dos. Es avalúo fiscal: base tributaria, en pesos y total. El
 * prompt lo prohíbe explícitamente y el mapa de campos no le pide valores a ese
 * documento, así que lo normal es que estos dos lleguen en `null` cuando el
 * expediente solo tiene el certificado.
 *
 * NO TIENE `nota_terreno`: es lo que anotó quien fue a ver la construcción, y
 * el modelo lee un papel.
 */
export interface ConstruccionPropuesta {
  /** El encabezado bajo el que el cuadro agrupa varias filas, o `null`. */
  grupo: string | null;
  tipo_edificacion: string;
  anio: number | null;
  /** EL MULTIPLICADOR de esta pestaña, en metros cuadrados. Decimal. */
  superficie_m2: number | null;
  /** La calidad o el material, transcrito literal del documento. */
  clase: string | null;
  vida_util_total: number | null;
  vida_util_residual: number | null;
  /** En UF por metro cuadrado. NUNCA el avalúo fiscal. */
  valor_reposicion_uf_m2: number | null;
  /** En UF por metro cuadrado. NUNCA el avalúo fiscal. */
  valor_residual_uf_m2: number | null;
  /** Con signo: un descuento llega NEGATIVO. */
  ajuste_comercial_pct: number | null;
  /** De qué documento dice la IA que salió esta fila. */
  fuente: FuenteDeLaPropuesta | null;
}

export interface PropuestaDeConstrucciones extends PropuestaDeRelleno {
  propuesta: {
    construcciones: ConstruccionPropuesta[];
    descripcion_general: string | null;
    /** Solo hay fuente si se propuso texto. */
    descripcion_general_fuente: FuenteDeLaPropuesta | null;
  };
}

export async function rellenarConstruccionesConIa(
  informeId: number,
): Promise<PropuestaDeConstrucciones> {
  return apiFetch<PropuestaDeConstrucciones>(
    `/api/informes/${informeId}/construcciones/rellenar`,
    { method: "POST" },
  );
}

/* --- "Descriptor de plantaciones" ------------------------------------------- */

/**
 * Una plantación PROPUESTA por el modelo.
 *
 * NO TIENE NI UN CAMPO DE VALOR, y esa es la diferencia de fondo con las otras
 * tres pestañas con cuadro: esto DESCRIBE el huerto, no lo tasa.
 *
 * `plantas_por_ha` ES LA DENSIDAD Y NO EL TOTAL. Un cuadro de tasación imprime
 * las dos columnas juntas ("1.111 pl/ha" y "4.777 plantas"), y tomar la segunda
 * multiplica por la superficie de nuevo. El total NO viaja: lo calcula el
 * servidor al guardar (superficie × densidad, con redondeo por fila).
 *
 * `superficie_ha` VA EN HECTÁREAS FIJAS y no en la unidad del informe, al revés
 * que el cuadro de clases de suelo: su columna vecina es la densidad en plantas
 * POR HECTÁREA, y el producto solo cierra si las dos hablan de lo mismo.
 *
 * `distancia_plantacion` ES TEXTO: "4,5 x 2,0" se lee como una sola expresión,
 * igual que se dicta en terreno.
 */
export interface PlantacionPropuesta {
  /** La especie, que es la clave con la que el cuadro AGRUPA las filas. */
  especie: string;
  variedad: string | null;
  anio_plantacion: number | null;
  /** En HECTÁREAS, siempre. Decimal. */
  superficie_ha: number | null;
  cuartel: string | null;
  portainjerto: string | null;
  /** El marco, como texto: "4,5 x 2,0". */
  distancia_plantacion: string | null;
  /** La DENSIDAD en plantas por hectárea. NUNCA el total del cuartel. */
  plantas_por_ha: number | null;
  sistema_riego: string | null;
  /** De qué documento dice la IA que salió esta fila. */
  fuente: FuenteDeLaPropuesta | null;
}

export interface PropuestaDePlantaciones extends PropuestaDeRelleno {
  propuesta: {
    plantaciones: PlantacionPropuesta[];
    descripcion_general: string | null;
    /** Solo hay fuente si se propuso texto. */
    descripcion_general_fuente: FuenteDeLaPropuesta | null;
  };
}

export async function rellenarPlantacionesConIa(
  informeId: number,
): Promise<PropuestaDePlantaciones> {
  return apiFetch<PropuestaDePlantaciones>(
    `/api/informes/${informeId}/plantaciones/rellenar`,
    { method: "POST" },
  );
}

/* --- "Descripción de la propiedad" ------------------------------------------ */

/**
 * Un deslinde PROPUESTO por el modelo, para UN rol que ya existe.
 *
 * NO ES UN `RolDeInforme`, y la diferencia es la que importa: esta pestaña no
 * crea, borra ni edita roles — solo le propone texto a uno que Antecedentes
 * generales ya cargó. Por eso trae `rol_sii` y `comuna`, que no se van a
 * guardar en ningún lado: son lo que la pantalla necesita para MOSTRAR a qué
 * bloque pertenece esta propuesta, antes de mezclarla con lo que ya hay.
 *
 * EL `id` NUNCA VIENE DEL MODELO. Al proveedor se le ofrecen ETIQUETAS
 * ("6-131 · Pirque") y el SERVIDOR las traduce contra los roles de ESE
 * informe — igual que `RellenadorDeSuelos` traduce el código de una clase al
 * id del catálogo. Un id que viniera del modelo podría apuntar al rol de OTRO
 * informe, y la propuesta se vería idéntica a una legítima.
 *
 * SIN `validada`, y no es un descuido: esa casilla dice que una PERSONA
 * revisó el texto, y un texto recién propuesto no lo tiene revisado por
 * definición. Dejar que el servidor mandara ese campo abriría la puerta a que
 * algún día llegara en `true`.
 */
export interface DeslindePropuesto {
  id: number;
  rol_sii: string;
  comuna: string | null;
  descripcion_deslindes: string | null;
  /** De qué documento dice la IA que salió el texto de este rol. */
  fuente: FuenteDeLaPropuesta | null;

  /* Datos legales del rol que la IA lee de los títulos y del avalúo del SII.
     SIN planimetría: se mide, no se lee. Opcionales: una respuesta anterior a
     esto no las trae, y `null` es «ningún documento lo dice». */
  nombre_predio?: string | null;
  superficie_titulos?: number | null;
  superficie_sii?: number | null;
  destino_sii?: string | null;
  avaluo_fiscal_clp?: number | null;
  inscripcion_fojas?: string | null;
  inscripcion_numero?: string | null;
  inscripcion_anio?: number | null;
  inscripcion_cbr?: string | null;
}

export interface PropuestaDeDescripcion extends PropuestaDeRelleno {
  propuesta: {
    roles: DeslindePropuesto[];
  };
}

export async function rellenarDescripcionConIa(informeId: number): Promise<PropuestaDeDescripcion> {
  return apiFetch<PropuestaDeDescripcion>(`/api/informes/${informeId}/descripcion/rellenar`, {
    method: "POST",
  });
}

/* --- Resolver una corrida, sea de la pestaña que sea ------------------------ */

/** Qué hizo la persona con la propuesta. Es la única medida de si sirve. */
export type ResultadoDelRelleno = "aceptado" | "descartado";

/**
 * Registra si la propuesta se usó o se descartó.
 *
 * NO SE ENCADENA AL GUARDADO NI LO BLOQUEA: es auditoría, y perder el registro
 * de una corrida no puede impedir que alguien guarde su trabajo. Quien llama
 * ignora el fallo a propósito.
 */
export function resolverElRelleno(
  ejecucionId: number,
  resultado: ResultadoDelRelleno,
): Promise<void> {
  return apiFetch<void>(`/api/ia/rellenos/${ejecucionId}`, {
    method: "PATCH",
    body: JSON.stringify({ resultado }),
  });
}
